import { Component, OnInit } from "@angular/core";
import {
  animate,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { ActivatedRoute } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { GlobalVariable } from "../app.component";
import { DataService, Response } from "../dataService/data.service";

type Sentiment = "positive" | "negative";

interface Stage {
  index: 1 | 2 | 3 | 4;
  label: string;
  hint: string;
}

interface BootstrapResponse {
  vertical: string;
  business_name: string;
  google_review_url: string | null;
  keywords: string[];
  word_pools: { positive: string[]; negative: string[] };
  others_word_pool: { positive: string[]; negative: string[] };
  others_keyword: string;
}

@Component({
  selector: "app-ai-review-experience-vertical",
  templateUrl: "./ai-review-experience-vertical.component.html",
  styleUrls: ["./ai-review-experience-vertical.component.scss"],
  animations: [
    trigger("expand", [
      transition(":enter", [
        style({ height: 0, opacity: 0 }),
        animate(
          "260ms cubic-bezier(.22,.61,.36,1)",
          style({ height: "*", opacity: 1 })
        ),
      ]),
      transition(":leave", [
        animate(
          "180ms ease",
          style({ height: 0, opacity: 0 })
        ),
      ]),
    ]),
  ],
})
export class AiReviewExperienceVerticalComponent implements OnInit {
  private BASE_URL = GlobalVariable;

  slug_value: string = "";
  outlet_id: string | null = null;
  loading: boolean = true;
  loadError: boolean = false;

  config: BootstrapResponse | null = null;
  step: 1 | 2 | 3 | 4 = 1;

  readonly stages: Stage[] = [
    { index: 1, label: "Rate",       hint: "How was it?" },
    { index: 2, label: "Highlights", hint: "What stood out?" },
    { index: 3, label: "Details",    hint: "Describe it" },
    { index: 4, label: "Post",       hint: "Review your review" },
  ];

  sentiment: Sentiment | null = null;
  selectedKeywords: string[] = [];
  othersText: string = "";
  selectedWords: string[] = [];

  reviewDraft: string = "";
  additionalText: string = "";
  isGenerating: boolean = false;
  isRefining: boolean = false;

  isEditingAdditional: boolean = false;
  additionalDraft: string = "";

  feedbackText: string = "";

  showThankYouModal: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private http: DataService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.route.params.forEach((val) => {
      this.slug_value = val["id"];
    });
    this.route.queryParamMap.subscribe((qp) => {
      this.outlet_id = qp.get("outlet_id");
    });
    this.bootstrap();
  }

  private bootstrap(): void {
    this.loading = true;
    const payload: any = { slug: this.slug_value };
    if (this.outlet_id) payload.outlet_id = this.outlet_id;
    this.http
      .postjson(this.BASE_URL.AI_REVIEW_BOOTSTRAP, payload)
      .subscribe(
        (res: Response) => {
          if (res.code === 200) {
            this.config =
              typeof res.data === "string" ? JSON.parse(res.data) : res.data;
            this.loadError = false;
          } else {
            this.loadError = true;
          }
          this.loading = false;
        },
        () => {
          this.loadError = true;
          this.loading = false;
        }
      );
  }

  get currentWordPool(): string[] {
    if (!this.config || !this.sentiment) return [];
    const base = this.config.word_pools[this.sentiment] || [];
    if (this.isOthersSelected) {
      const extras = this.config.others_word_pool[this.sentiment] || [];
      return [...base, ...extras.filter((w) => !base.includes(w))];
    }
    return base;
  }

  get isOthersSelected(): boolean {
    return (
      this.config != null &&
      this.selectedKeywords.includes(this.config.others_keyword)
    );
  }

  get canProceedFromScreen2(): boolean {
    if (this.selectedKeywords.length === 0) return false;
    if (this.isOthersSelected && this.othersText.trim().length === 0)
      return false;
    return true;
  }

  // Summary text shown when a stage collapses
  summaryFor(stageIndex: 1 | 2 | 3 | 4): string {
    if (stageIndex === 1) {
      return this.sentiment === "positive"
        ? "Good experience"
        : this.sentiment === "negative"
        ? "Needs improvement"
        : "";
    }
    if (stageIndex === 2) {
      const list = this.selectedKeywords.map((kw) =>
        kw === this.config?.others_keyword && this.othersText
          ? this.othersText
          : kw
      );
      return list.join(" · ");
    }
    if (stageIndex === 3) {
      return this.selectedWords.join(" · ");
    }
    return "";
  }

  goNext(): void {
    if (this.step === 1) {
      if (!this.sentiment) return;
      this.step = 2;
    } else if (this.step === 2) {
      if (!this.canProceedFromScreen2) return;
      this.step = 3;
    } else if (this.step === 3) {
      this.step = 4;
      if (this.sentiment !== "negative") {
        this.generate();
      }
    }
  }

  openAdditionalEditor(): void {
    this.additionalDraft = this.additionalText;
    this.isEditingAdditional = true;
  }

  cancelAdditionalEditor(): void {
    this.isEditingAdditional = false;
    this.additionalDraft = "";
  }

  saveAdditionalEditor(): void {
    const next = (this.additionalDraft || "").trim();
    const changed = next !== this.additionalText.trim();
    this.additionalText = next;
    this.isEditingAdditional = false;
    this.additionalDraft = "";
    if (changed) this.generate();
  }

  // Allow tapping a completed stage to re-open it (and lock later stages).
  jumpTo(stageIndex: 1 | 2 | 3 | 4): void {
    if (stageIndex >= this.step) return; // can't jump forward
    this.step = stageIndex;
  }

  selectSentiment(s: Sentiment): void {
    this.sentiment = s;
    this.selectedWords = [];
    setTimeout(() => this.goNext(), 0);
  }

  toggleKeyword(kw: string): void {
    const idx = this.selectedKeywords.indexOf(kw);
    if (idx >= 0) this.selectedKeywords.splice(idx, 1);
    else this.selectedKeywords.push(kw);
  }
  isKeywordSelected(kw: string): boolean {
    return this.selectedKeywords.includes(kw);
  }

  toggleWord(w: string): void {
    const idx = this.selectedWords.indexOf(w);
    if (idx >= 0) {
      this.selectedWords.splice(idx, 1);
    } else {
      if (this.selectedWords.length >= 3) {
        this.toastr.info("You can select up to 3 words");
        return;
      }
      this.selectedWords.push(w);
    }
  }
  isWordSelected(w: string): boolean {
    return this.selectedWords.includes(w);
  }

  get combinedOthersText(): string {
    const parts = [this.othersText, this.additionalText]
      .map((p) => (p || "").trim())
      .filter((p) => p.length > 0);
    return parts.join(". ");
  }

  generate(): void {
    if (!this.sentiment) return;
    this.isGenerating = true;
    this.reviewDraft = "";
    const payload: any = {
      slug: this.slug_value,
      sentiment: this.sentiment,
      selected_keywords: this.selectedKeywords,
      selected_words: this.selectedWords,
      others_text: this.combinedOthersText,
    };
    if (this.outlet_id) payload.outlet_id = this.outlet_id;
    this.http.postjson(this.BASE_URL.AI_REVIEW_GENERATE, payload).subscribe(
      (res: Response) => {
        if (res.code === 200) {
          this.reviewDraft = res.data["review_draft"];
        } else {
          this.toastr.error("Couldn't generate the review. Try again.");
        }
        this.isGenerating = false;
      },
      () => {
        this.toastr.error("Couldn't generate the review. Try again.");
        this.isGenerating = false;
      }
    );
  }

  refine(mode: "refine" | "short" | "long"): void {
    if (!this.reviewDraft.trim()) return;
    this.isRefining = true;
    const payload = { slug: this.slug_value, draft: this.reviewDraft, mode };
    this.http.postjson(this.BASE_URL.AI_REVIEW_REFINE, payload).subscribe(
      (res: Response) => {
        if (res.code === 200) {
          this.reviewDraft = res.data["review_draft"];
        } else {
          this.toastr.error("Couldn't refine the review. Try again.");
        }
        this.isRefining = false;
      },
      () => {
        this.toastr.error("Couldn't refine the review. Try again.");
        this.isRefining = false;
      }
    );
  }

  submit(): void {
    if (this.sentiment === "negative") {
      this.captureNegativeFeedback();
      return;
    }
    this.copyAndPostToGoogle();
  }

  private captureNegativeFeedback(): void {
    // eslint-disable-next-line no-console
    console.log("[AI Review Vertical] Negative feedback captured (mockup):", {
      slug: this.slug_value,
      sentiment: this.sentiment,
      selected_keywords: this.selectedKeywords,
      selected_words: this.selectedWords,
      others_text: this.othersText,
      feedback_text: this.feedbackText,
    });
    this.showThankYouModal = true;
  }

  closeThankYou(): void {
    this.showThankYouModal = false;
  }

  copyAndPostToGoogle(): void {
    if (!this.reviewDraft.trim()) {
      this.toastr.info("Nothing to copy yet");
      return;
    }
    this.copyToClipboard(this.reviewDraft);
    this.toastr.success("Copied. Opening Google review page...");

    const url = this.config?.google_review_url;
    if (url) {
      setTimeout(() => window.location.replace(url), 300);
    } else {
      this.toastr.warning("Google review link is not configured for this location.");
    }
  }

  private copyToClipboard(text: string): void {
    if (navigator.clipboard && (window as any).isSecureContext) {
      navigator.clipboard.writeText(text).catch(() => this.fallbackCopy(text));
    } else {
      this.fallbackCopy(text);
    }
  }

  private fallbackCopy(text: string): void {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try { document.execCommand("copy"); } catch { /* best-effort */ }
    document.body.removeChild(ta);
  }
}
